import React from "react";
import { Platform, StyleSheet } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { FullWindowOverlay } from "react-native-screens";

type RootOverlayHostProps = {
    children: React.ReactNode;
    /**
     * "blocking" — the overlay captures touches across the whole window (e.g. a modal backdrop).
     * "passthrough" — only the actual children capture touches; empty areas let touches reach the
     * content behind (e.g. a bottom-anchored snackbar).
     */
    pointerMode?: "blocking" | "passthrough";
};

/**
 * Hosts a root-mounted JS overlay so it renders ABOVE native modals on iOS.
 *
 * On iOS a `@react-navigation/native-stack` modal (`presentation: "modal" | "formSheet"`) is a
 * separate `UIViewController` presented over the root VC, so any overlay mounted in the root React
 * tree paints BEHIND it. `FullWindowOverlay` renders in a window above the whole VC stack, escaping
 * that — it needs its own `GestureHandlerRootView` for touches/gestures to work (the overlay window
 * sits outside the root one). This mirrors the proven pattern in
 * `components/select/SelectChooser.tsx`.
 *
 * On Android root overlays already paint above native-stack fragments, and on web the "native"
 * modals are themselves JS cards the portal already covers — so both are a plain passthrough
 * (`FullWindowOverlay` is a no-op off iOS anyway and would only `console.warn`).
 */
const RootOverlayHost = ({ children, pointerMode = "blocking" }: RootOverlayHostProps) => {
    if (Platform.OS !== "ios") {
        return <>{children}</>;
    }
    return (
        <FullWindowOverlay>
            <GestureHandlerRootView
                style={StyleSheet.absoluteFill}
                pointerEvents={pointerMode === "blocking" ? "auto" : "box-none"}
            >
                {children}
            </GestureHandlerRootView>
        </FullWindowOverlay>
    );
};

export default RootOverlayHost;
